import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatTime, formatDate } from "@/lib/datetime";
import {
    Pulse, MagnifyingGlass, PhoneIncoming, PhoneOutgoing, PhoneX, WhatsappLogo,
    Play, ChatCircleText, User, Sparkle,
} from "@phosphor-icons/react";

const fmtDur = (s) => {
    s = s || 0;
    const m = Math.floor(s / 60), r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
};

function CallIcon({ item }) {
    if (item.status === "failed" || item.status === "no-answer" || item.status === "missed")
        return <PhoneX size={16} weight="fill" className="text-red-500" />;
    return item.direction === "outbound"
        ? <PhoneOutgoing size={16} weight="fill" className="text-emerald-600" />
        : <PhoneIncoming size={16} weight="fill" className="text-sky-600" />;
}

function TimelineItem({ item, tz }) {
    const isCall = item.kind === "call";
    return (
        <div className="relative pl-10" data-testid={`activity-item-${item.id}`}>
            <span className="absolute left-[11px] top-1 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-white bg-stone-300 shadow" />
            <div className="absolute left-0 top-0 grid h-6 w-6 place-items-center rounded-full bg-white ring-1 ring-stone-200">
                {isCall ? <CallIcon item={item} /> : <WhatsappLogo size={15} weight="fill" className="text-emerald-600" />}
            </div>
            <div className="rounded-xl border border-stone-200 bg-white p-3.5 shadow-sm transition-all duration-200 hover:shadow-md">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-ink">
                        {isCall
                            ? `${item.direction === "outbound" ? "Outbound" : "Inbound"} call`
                            : `WhatsApp ${item.direction === "out" ? "sent" : "received"}`}
                    </div>
                    <div className="shrink-0 text-[11px] text-stone2">{item.timestamp ? formatTime(item.timestamp, tz) : ""}</div>
                </div>

                {isCall ? (
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${item.status === "completed" ? "bg-emerald-100 text-emerald-700" : item.status === "failed" || item.status === "no-answer" ? "bg-red-100 text-red-600" : "bg-stone-100 text-stone2"}`}>{item.status}</span>
                        {item.duration_sec > 0 && <span>⏱ {fmtDur(item.duration_sec)}</span>}
                        {item.agent && <span>· {item.agent}</span>}
                        {item.recording_url && (
                            <a href={item.recording_url} target="_blank" rel="noreferrer"
                               className="inline-flex items-center gap-1 font-semibold text-emerald-700 hover:underline" data-testid={`activity-rec-${item.id}`}>
                                <Play size={12} weight="fill" /> Recording
                            </a>
                        )}
                    </div>
                ) : (
                    <div className="mt-1 whitespace-pre-wrap break-words text-sm text-ink/90">
                        {item.template_name && <span className="mr-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-700">tpl</span>}
                        {item.body || `[${item.type}]`}
                    </div>
                )}

                {isCall && item.summary && (
                    <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-stone-50 p-2 text-xs text-stone2">
                        <Sparkle size={13} className="mt-0.5 shrink-0 text-amber-500" weight="fill" />
                        <span>{item.summary}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function Activity() {
    const { orgTimezone } = useAuth();
    const [contacts, setContacts] = useState([]);
    const [active, setActive] = useState(null);
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadingT, setLoadingT] = useState(false);

    useEffect(() => {
        api.get("/activity/contacts").then(({ data }) => {
            setContacts(data);
            if (data.length) setActive(data[0]);
        }).finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!active) return;
        setLoadingT(true);
        api.get(`/activity/${active.contact}`).then(({ data }) => setItems(data)).finally(() => setLoadingT(false));
    }, [active]);

    const filtered = contacts.filter((c) => !q || (c.display || "").includes(q));

    // group timeline items by date
    const groups = [];
    let lastDate = null;
    items.forEach((it) => {
        const d = (it.timestamp || "").slice(0, 10);
        if (d !== lastDate) { groups.push({ date: it.timestamp, list: [] }); lastDate = d; }
        groups[groups.length - 1].list.push(it);
    });

    return (
        <AppShell>
            <div className="mx-auto max-w-7xl px-6 py-8 md:px-12 md:py-10">
                <div className="mb-6">
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-600">
                        <Pulse size={16} weight="fill" /> Customer 360
                    </div>
                    <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight sm:text-5xl">Activity</h1>
                    <p className="mt-2 text-sm text-stone2">Every call and WhatsApp message for a contact — in one timeline.</p>
                </div>

                <div className="flex h-[calc(100vh-15rem)] min-h-[560px] overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                    {/* contacts */}
                    <div className="flex w-full max-w-[320px] shrink-0 flex-col border-r border-stone-200">
                        <div className="border-b border-stone-100 p-4">
                            <div className="relative">
                                <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                                <input data-testid="activity-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts…"
                                    className="w-full rounded-full border border-stone-300 bg-white py-2 pl-9 pr-3 text-sm outline-none transition-all focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto" data-testid="activity-contact-list">
                            {loading && <div className="p-6 text-center text-sm text-stone2">Loading…</div>}
                            {!loading && filtered.length === 0 && <div className="p-8 text-center text-sm text-stone2">No contacts yet.</div>}
                            {filtered.map((c) => (
                                <button key={c.contact} data-testid={`activity-contact-${c.contact}`} onClick={() => setActive(c)}
                                    className={`flex w-full items-center gap-3 border-l-2 px-4 py-3 text-left transition-colors duration-200 hover:bg-stone-50 ${active?.contact === c.contact ? "border-l-emerald-500 bg-emerald-50" : "border-l-transparent"}`}>
                                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-stone-100 text-stone2"><User size={18} /></div>
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-sm font-semibold text-ink">{c.display}</div>
                                        <div className="mt-0.5 flex items-center gap-1.5">
                                            {c.channels.includes("call") && <PhoneOutgoing size={11} className="text-stone-400" />}
                                            {c.channels.includes("whatsapp") && <WhatsappLogo size={11} weight="fill" className="text-emerald-500" />}
                                            <span className="text-[10px] text-stone-400">{c.last_ts ? formatDate(c.last_ts, orgTimezone) : ""}</span>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* timeline */}
                    <div className="flex-1 overflow-y-auto bg-stone-50 p-6" data-testid="activity-timeline">
                        {!active ? (
                            <div className="grid h-full place-items-center text-sm text-stone2">Select a contact to see their history.</div>
                        ) : (
                            <>
                                <div className="mb-5 flex items-center gap-3">
                                    <div className="grid h-12 w-12 place-items-center rounded-full bg-ink text-white"><User size={22} /></div>
                                    <div>
                                        <div className="font-heading text-xl font-bold text-ink" data-testid="activity-active-contact">{active.display}</div>
                                        <div className="text-xs text-stone2">{items.length} activities</div>
                                    </div>
                                </div>
                                {loadingT && <div className="text-center text-sm text-stone2">Loading timeline…</div>}
                                {!loadingT && items.length === 0 && <div className="text-center text-sm text-stone2">No activity yet.</div>}
                                <div className="relative">
                                    <span className="absolute left-[11px] top-0 h-full w-px bg-stone-200" />
                                    <div className="space-y-6">
                                        {groups.map((g, gi) => (
                                            <div key={gi} className="space-y-3">
                                                <div className="relative pl-10">
                                                    <span className="inline-block rounded-full bg-stone-200 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-stone-600">{g.date ? formatDate(g.date, orgTimezone) : ""}</span>
                                                </div>
                                                {g.list.map((it) => <TimelineItem key={it.id} item={it} tz={orgTimezone} />)}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
